import { addMinutes, subMinutes } from 'date-fns';
import { getHoursInRange } from '~/utils/date';

/**
 * Las franjas de media hora que ocupa una reserva en un espacio SIN hora de
 * fin pero con bloque de tiempo (`blockTimeMinutes`; en NOI, la visita
 * "Conocer Coworking": 60 minutos).
 *
 * Es, tal cual, la cuenta que el guardado (`onSubmit`) hacía en línea desde
 * siempre y con la que están escritas todas las reservas de ese espacio:
 *
 *  · el bloque de la visita (`inicio` … `inicio + bloque`),
 *  · más **media hora de margen ANTES**, salvo cuando se empieza a la primera
 *    hora del día (no hay antes),
 *  · y la última franja se descarta si no es la última del horario (la hora
 *    de fin no se ocupa, igual que en las salas con hora de fin).
 *
 * Así, a las 10:00 con bloque de 60 se ocupan 09:30, 10:00 y 10:30.
 *
 * Se saca aquí para que **el desplegable de horas y el guardado usen la misma
 * cuenta**. Cuando no la usaban, el desplegable ofrecía las 11:00 con las
 * 10:00 ya tomadas (solo miraba la franja de las 11:00), el guardado exigía
 * también las 10:30 y la reserva se rechazaba como "acaba de ser reservada
 * por otra persona", con la reserva recién creada cancelada por el sistema.
 */
export const franjasDeBloque = ({
  startHour,
  blockTimeMinutes,
  primeraHora,
  ultimaHora,
}: {
  /** Hora de inicio elegida, `HH:mm`. */
  startHour: string;
  blockTimeMinutes: number;
  /** Primera y última hora del horario de ese día, `HH:mm`. */
  primeraHora: string;
  ultimaHora: string;
}): string[] => {
  const inicio = new Date(`2023-01-01T${startHour}`);
  const empiezaElDia = startHour === primeraHora;
  const desde = empiezaElDia ? inicio : subMinutes(inicio, blockTimeMinutes);
  const hasta = addMinutes(inicio, blockTimeMinutes);
  const aTexto = (d: Date) =>
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

  const franjas = getHoursInRange(aTexto(desde), aTexto(hasta));
  if (!empiezaElDia) franjas.shift();
  if (!franjas.includes(ultimaHora)) franjas.pop();
  return franjas;
};
